import React, { useState, useEffect, useMemo, useRef, useId, forwardRef } from 'react';
import { Box, Typography, Button, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PaymentsIcon from '@mui/icons-material/Payments';
import GroupsIcon from '@mui/icons-material/Groups';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ShareIcon from '@mui/icons-material/Share';
import { Stripe, PayPal, Wise, Venmo } from '../../../Utils/icons';
import blitzBoltBlack from '../../../Components/globalAssets/blitzBoltBlack.png';

// Payment provider logos as simple styled boxes - responsive size
const PaymentLogo = ({ name, color, icon }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: { xs: 0.5, sm: 0.75 },
      px: { xs: 1, sm: 1.5 },
      py: { xs: 0.5, sm: 0.75 },
      borderRadius: '6px',
      background: 'white',
      border: '1px solid rgba(0,0,0,0.08)',
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      minWidth: 'fit-content',
    }}
  >
    <Box sx={{ color, fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: 'flex', '& svg': { width: { xs: 12, sm: 16 }, height: { xs: 12, sm: 16 } } }}>{icon}</Box>
    <Typography sx={{ fontWeight: 600, fontSize: { xs: '0.625rem', sm: '0.75rem' }, color: '#1e293b' }}>
      {name}
    </Typography>
  </Box>
);

// Animated Marquee Component
const Marquee = ({ children, direction = 'left', duration = 20 }) => (
  <Box sx={{ overflow: 'hidden', width: '100%' }}>
    <motion.div
      animate={{ x: direction === 'left' ? ['0%', '-50%'] : ['-50%', '0%'] }}
      transition={{ duration, repeat: Infinity, ease: 'linear' }}
      style={{ display: 'flex', gap: '12px', width: 'fit-content' }}
    >
      {children}
      {children}
    </motion.div>
  </Box>
);

// Animated Notification Item - Light theme with spring animation - responsive
const NotificationItem = ({ title, subtitle, time, icon, iconBg }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: { xs: 1.5, sm: 2 },
      p: { xs: 1.5, sm: 2 },
      borderRadius: { xs: '10px', sm: '14px' },
      background: 'white',
      border: '1px solid rgba(0, 0, 0, 0.06)',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.03)',
      width: '100%',
    }}
  >
    {/* Icon */}
    <Box
      sx={{
        width: { xs: 32, sm: 42 },
        height: { xs: 32, sm: 42 },
        borderRadius: { xs: '8px', sm: '12px' },
        background: iconBg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        fontSize: { xs: '0.875rem', sm: '1.1rem' },
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}
    >
      {icon}
    </Box>
    
    {/* Content */}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, mb: 0.25, flexWrap: 'wrap' }}>
        <Typography 
          sx={{ 
            fontWeight: 600, 
            fontSize: { xs: '0.75rem', sm: '0.875rem' }, 
            color: '#1e293b',
            letterSpacing: '-0.01em',
          }}
        >
          {title}
        </Typography>
        <Typography 
          sx={{ 
            fontSize: { xs: '0.625rem', sm: '0.75rem' }, 
            color: '#94a3b8',
          }}
        >
          · {time}
        </Typography>
      </Box>
      <Typography 
        sx={{ 
          fontSize: { xs: '0.6875rem', sm: '0.8rem' }, 
          color: '#64748b',
          lineHeight: 1.4,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {subtitle}
      </Typography>
    </Box>
  </Box>
);

// Animated List Item wrapper with spring animation
const AnimatedListItem = ({ children }) => (
  <motion.div
    initial={{ scale: 0, opacity: 0 }}
    animate={{ scale: 1, opacity: 1, originY: 0 }}
    exit={{ scale: 0, opacity: 0 }}
    transition={{ type: 'spring', stiffness: 350, damping: 40 }}
    layout
    style={{ width: '100%' }}
  >
    {children}
  </motion.div>
);

// Animated List Component - shows items one by one
const AnimatedList = ({ children, delay = 1200 }) => {
  const [index, setIndex] = useState(0);
  const childrenArray = useMemo(() => React.Children.toArray(children), [children]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIndex((prevIndex) => (prevIndex + 1) % childrenArray.length);
    }, delay);

    return () => clearTimeout(timeout);
  }, [index, delay, childrenArray.length]);

  const itemsToShow = useMemo(() => {
    return childrenArray.slice(0, index + 1).reverse();
  }, [index, childrenArray]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: { xs: 1, sm: 1.5 },
        width: '100%',
      }}
    >
      <AnimatePresence>
        {itemsToShow.map((item) => (
          <AnimatedListItem key={(item).key}>
            {item}
          </AnimatedListItem>
        ))}
      </AnimatePresence>
    </Box>
  );
};

// Animated Beam Component - connects two ref elements with animated gradient
const AnimatedBeam = ({
  containerRef,
  fromRef,
  toRef,
  curvature = 0,
  reverse = false,
  duration = 4,
  delay = 0,
  pathColor = 'rgba(255,255,255,0.15)',
  pathWidth = 1.5,
  gradientStartColor = '#ffaa40',
  gradientStopColor = '#9c40ff',
  endYOffset = 0,
}) => {
  const id = useId();
  const [pathD, setPathD] = useState('');
  const [svgDimensions, setSvgDimensions] = useState({ width: 0, height: 0 });

  const gradientCoordinates = reverse
    ? { x1: ['90%', '-10%'], x2: ['100%', '0%'], y1: ['0%', '0%'], y2: ['0%', '0%'] }
    : { x1: ['10%', '110%'], x2: ['0%', '100%'], y1: ['0%', '0%'], y2: ['0%', '0%'] };

  useEffect(() => {
    const updatePath = () => {
      if (containerRef.current && fromRef.current && toRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const rectA = fromRef.current.getBoundingClientRect();
        const rectB = toRef.current.getBoundingClientRect();

        const svgWidth = containerRect.width;
        const svgHeight = containerRect.height;
        setSvgDimensions({ width: svgWidth, height: svgHeight });

        const startX = rectA.left - containerRect.left + rectA.width / 2;
        const startY = rectA.top - containerRect.top + rectA.height / 2;
        const endX = rectB.left - containerRect.left + rectB.width / 2;
        const endY = rectB.top - containerRect.top + rectB.height / 2 + endYOffset;

        const controlY = startY - curvature;
        const d = `M ${startX},${startY} Q ${(startX + endX) / 2},${controlY} ${endX},${endY}`;
        setPathD(d);
      }
    };

    const resizeObserver = new ResizeObserver(() => updatePath());
    if (containerRef.current) resizeObserver.observe(containerRef.current);
    updatePath();
    return () => resizeObserver.disconnect();
  }, [containerRef, fromRef, toRef, curvature, endYOffset]);

  return (
    <svg
      fill="none"
      width={svgDimensions.width}
      height={svgDimensions.height}
      xmlns="http://www.w3.org/2000/svg"
      style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
      viewBox={`0 0 ${svgDimensions.width} ${svgDimensions.height}`}
    >
      <path d={pathD} stroke={pathColor} strokeWidth={pathWidth} strokeLinecap="round" />
      <path d={pathD} strokeWidth={pathWidth} stroke={`url(#${id})`} strokeOpacity="1" strokeLinecap="round" />
      <defs>
        <motion.linearGradient
          id={id}
          gradientUnits="userSpaceOnUse"
          initial={{ x1: '0%', x2: '0%', y1: '0%', y2: '0%' }}
          animate={{ x1: gradientCoordinates.x1, x2: gradientCoordinates.x2, y1: gradientCoordinates.y1, y2: gradientCoordinates.y2 }}
          transition={{ delay, duration, ease: [0.16, 1, 0.3, 1], repeat: Infinity, repeatDelay: 0 }}
        >
          <stop stopColor={gradientStartColor} stopOpacity="0" />
          <stop stopColor={gradientStartColor} />
          <stop offset="32.5%" stopColor={gradientStopColor} />
          <stop offset="100%" stopColor={gradientStopColor} stopOpacity="0" />
        </motion.linearGradient>
      </defs>
    </svg>
  );
};

// Circle component for beam nodes - responsive
const Circle = forwardRef(({ children, size = 48, mobileSize = 36, bg = 'white', border = true }, ref) => (
  <Box
    ref={ref}
    sx={{
      width: { xs: mobileSize, sm: size },
      height: { xs: mobileSize, sm: size },
      borderRadius: '50%',
      background: bg,
      border: border ? '2px solid rgba(0,0,0,0.08)' : 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 0 20px -12px rgba(0,0,0,0.3)',
      zIndex: 10,
      '& svg': { width: { xs: mobileSize * 0.4, sm: size * 0.4 }, height: { xs: mobileSize * 0.4, sm: size * 0.4 } },
    }}
  >
    {children}
  </Box>
));

// Integrations Background Component with proper refs - responsive
const IntegrationsBackground = () => {
  const containerRef = useRef(null);
  const userRef = useRef(null);
  const blitzRef = useRef(null);
  const stripeRef = useRef(null);
  const paypalRef = useRef(null);
  const wiseRef = useRef(null);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(145deg, #f8f9fa 0%, #ffffff 50%, #f8f9fa 100%)',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 1.5, sm: 2, md: 4 },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: { xs: 280, sm: 350, md: 450 },
          height: { xs: 140, sm: 170, md: 200 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: 'space-between',
          gap: { xs: 1.5, sm: 2, md: 2.5 },
        }}
      >
        {/* Row 1 - Only right icon (Stripe) */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ width: { xs: 36, sm: 48 } }} />
          <Box sx={{ width: { xs: 48, sm: 64 } }} />
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.4, type: 'spring' }}>
            <Circle ref={stripeRef} size={48} mobileSize={36} bg="white" border={false}>
              <Stripe />
            </Circle>
          </motion.div>
        </Box>

        {/* Row 2 - User (left), Blitz (center), PayPal (right) */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: 'spring' }}>
            <Circle ref={userRef} size={48} mobileSize={36} bg="rgba(0,0,0,0.04)" border>
              <PersonOutlineIcon sx={{ fontSize: { xs: 18, sm: 24 }, color: 'rgba(0,0,0,0.6)' }} />
            </Circle>
          </motion.div>
          
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.1, type: 'spring' }}>
            <Circle ref={blitzRef} size={48} mobileSize={36} bg="rgba(0,0,0,0.06)" border>
              <img src={blitzBoltBlack} alt="Blitz" style={{ width: 'auto', height: 'auto', maxWidth: '60%', maxHeight: '60%', objectFit: 'contain' }} />
            </Circle>
          </motion.div>
          
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.5, type: 'spring' }}>
            <Circle ref={paypalRef} size={48} mobileSize={36} bg="white" border={false}>
              <PayPal />
            </Circle>
          </motion.div>
        </Box>

        {/* Row 3 - Only right icon (Wise) */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ width: { xs: 36, sm: 48 } }} />
          <Box sx={{ width: { xs: 48, sm: 64 } }} /> 
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.6, type: 'spring' }}>
            <Circle ref={wiseRef} size={48} mobileSize={36} bg="white" border={false}>
              <Wise />
            </Circle>
          </motion.div>
        </Box>
      </Box>

      {/* Animated Beams */}
      <AnimatedBeam 
        containerRef={containerRef} 
        fromRef={userRef} 
        toRef={blitzRef}
        pathColor="rgba(0,0,0,0.1)"
        gradientStartColor="#ffaa40"
        gradientStopColor="#9c40ff"
      />
      <AnimatedBeam 
        containerRef={containerRef} 
        fromRef={blitzRef} 
        toRef={stripeRef} 
        curvature={0}
        reverse 
        pathColor="rgba(0,0,0,0.1)"
        gradientStartColor="#ffaa40"
        gradientStopColor="#9c40ff"
      />
      <AnimatedBeam 
        containerRef={containerRef} 
        fromRef={blitzRef} 
        toRef={paypalRef} 
        curvature={0}
        reverse 
        pathColor="rgba(0,0,0,0.1)"
        gradientStartColor="#ffaa40"
        gradientStopColor="#9c40ff"
      />
      <AnimatedBeam 
        containerRef={containerRef} 
        fromRef={blitzRef} 
        toRef={wiseRef} 
        curvature={0}
        reverse 
        pathColor="rgba(0,0,0,0.1)"
        gradientStartColor="#ffaa40"
        gradientStopColor="#9c40ff"
      />
    </Box>
  );
};

// Bento Grid Container - responsive
const BentoGrid = ({ children }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
      gridAutoRows: { xs: 'auto', lg: '22rem' },
      gap: { xs: 1.5, sm: 2 },
      width: '100%',
    }}
  >
    {children}
  </Box>
);

// Bento Card Component - responsive
const BentoCard = ({ 
  name, 
  description, 
  Icon, 
  background,
  href = '#',
  cta = 'Learn more',
  className = '',
  isDark = false,
}) => (
  <Box
    sx={{
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      overflow: 'hidden',
      borderRadius: { xs: '12px', sm: '16px' },
      background: isDark ? '#0f0f0f' : 'white',
      boxShadow: isDark 
        ? '0 0 0 1px rgba(255,255,255,0.05), 0 -20px 80px -20px rgba(255,255,255,0.1) inset'
        : '0 0 0 1px rgba(0,0,0,0.03), 0 2px 4px rgba(0,0,0,0.05), 0 12px 24px rgba(0,0,0,0.05)',
      border: isDark ? '1px solid rgba(255,255,255,0.08)' : 'none',
      gridColumn: className.includes('col-span-2') ? { xs: 'span 1', sm: 'span 2', lg: 'span 2' } : 'span 1',
      gridRow: className.includes('row-span-2') ? { xs: 'span 1', lg: 'span 2' } : 'span 1',
      minHeight: { xs: '280px', sm: '300px', lg: 'auto' },
      cursor: 'pointer',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
      '&:hover': {
        transform: { xs: 'none', sm: 'translateY(-4px)' },
        boxShadow: isDark
          ? '0 0 0 1px rgba(255,255,255,0.08), 0 -20px 80px -20px rgba(255,255,255,0.15) inset'
          : '0 0 0 1px rgba(0,0,0,0.03), 0 4px 8px rgba(0,0,0,0.08), 0 24px 48px rgba(0,0,0,0.08)',
      },
      '&:hover .bento-icon': {
        transform: { xs: 'none', sm: 'scale(0.75)' },
      },
      '&:hover .bento-content': {
        transform: { lg: 'translateY(-40px)' },
      },
      '&:hover .bento-cta': {
        opacity: 1,
        transform: 'translateY(0)',
      },
      '&:hover .bento-overlay': {
        background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
      },
    }}
  >
    {/* Background */}
    <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden', minHeight: { xs: 120, sm: 150 } }}>
      {background}
    </Box>

    {/* Content */}
    <Box
      className="bento-content"
      sx={{
        p: { xs: 2, sm: 2.5, md: 3 },
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        transition: 'transform 0.3s ease',
        zIndex: 10,
        background: isDark ? 'transparent' : 'white',
      }}
    >
      <Box
        className="bento-icon"
        sx={{
          transition: 'transform 0.3s ease',
          transformOrigin: 'left center',
        }}
      >
        <Icon sx={{ fontSize: { xs: 28, sm: 32, md: 40 }, color: isDark ? 'rgba(255,255,255,0.6)' : '#334155' }} />
      </Box>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: isDark ? 'white' : '#1e293b',
          fontSize: { xs: '0.9375rem', sm: '1rem', md: '1.125rem' },
        }}
      >
        {name}
      </Typography>
      <Typography
        sx={{
          color: isDark ? 'rgba(255,255,255,0.5)' : '#64748b',
          fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
          lineHeight: 1.5,
        }}
      >
        {description}
      </Typography>
    </Box>

    {/* CTA - shows on hover (desktop), always visible on mobile */}
    <Box
      className="bento-cta"
      sx={{
        position: { xs: 'relative', lg: 'absolute' },
        bottom: 0,
        left: 0,
        right: 0,
        p: { xs: 2, sm: 2.5, md: 3 },
        pt: { xs: 0, lg: 3 },
        opacity: { xs: 1, lg: 0 },
        transform: { xs: 'none', lg: 'translateY(10px)' },
        transition: 'all 0.3s ease',
        zIndex: 11,
        background: isDark ? 'transparent' : 'white',
      }}
    >
      <Button
        href={href}
        endIcon={<ArrowForwardIcon sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }} />}
        sx={{
          color: isDark ? 'white' : '#0f172a',
          fontWeight: 600,
          fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
          p: 0,
          minWidth: 'auto',
          '&:hover': {
            background: 'transparent',
            color: '#3b82f6',
          },
        }}
      >
        {cta}
      </Button>
    </Box>

    {/* Hover overlay */}
    <Box
      className="bento-overlay"
      sx={{
        position: 'absolute',
        inset: 0,
        transition: 'background 0.3s ease',
        pointerEvents: 'none',
      }}
    />
  </Box>
);

// Payment methods data for marquee
const paymentMethods = [
  { name: 'Stripe', color: '#635bff', icon: <Stripe /> },
  { name: 'PayPal', color: '#003087', icon: <PayPal /> },
  { name: 'Bank', color: '#1e293b', icon: <AccountBalanceIcon sx={{ fontSize: 16 }} /> },
  { name: 'Wise', color: '#9fe870', icon: <Wise /> },
  { name: 'Venmo', color: '#008cff', icon: <Venmo /> },
  { name: 'ACH', color: '#0f172a', icon: <CreditCardIcon sx={{ fontSize: 16 }} /> },
];

// Notification data - payment platform relevant
const notifications = [
  { 
    title: 'Payment sent', 
    subtitle: '$2,500 to @creator_jane', 
    time: 'Just now',
    icon: '💸',
    iconBg: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
  },
  { 
    title: 'New creator joined', 
    subtitle: 'Alex Rivera accepted invite', 
    time: '2m ago',
    icon: '🎉',
    iconBg: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
  },
  { 
    title: 'Campaign live', 
    subtitle: 'Summer Collection is now active', 
    time: '5m ago',
    icon: '🚀',
    iconBg: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
  },
  { 
    title: 'Payout complete', 
    subtitle: 'Batch payment to 8 creators', 
    time: '12m ago',
    icon: '✅',
    iconBg: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
  },
];

// Features data
const features = [
  {
    Icon: PaymentsIcon,
    name: 'Instant Payouts',
    description: 'Pay creators in seconds, not days. Support for 50+ currencies worldwide.',
    href: '#pay',
    cta: 'Explore Payments',
    className: 'col-span-1',
    background: (
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          pt: { xs: 3, sm: 4 },
          px: { xs: 1.5, sm: 2 },
          background: 'linear-gradient(180deg, rgba(59, 130, 246, 0.04) 0%, transparent 100%)',
          maskImage: 'linear-gradient(to bottom, black 60%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to bottom, black 60%, transparent 100%)',
        }}
      >
        <Marquee duration={25}>
          {paymentMethods.map((p, i) => (
            <PaymentLogo key={i} {...p} />
          ))}
        </Marquee>
        <Box sx={{ mt: { xs: 1.5, sm: 2 } }}>
          <Marquee direction="right" duration={30}>
            {[...paymentMethods].reverse().map((p, i) => (
              <PaymentLogo key={i} {...p} />
            ))}
          </Marquee>
        </Box>
      </Box>
    ),
  },
  {
    Icon: NotificationsActiveIcon,
    name: 'Real-time Notifications',
    description: 'Get notified instantly when payments, campaigns, or creators need attention.',
    href: '#notifications',
    cta: 'Learn more',
    className: 'col-span-2',
    background: (
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          borderRadius: { xs: '12px', sm: '16px' },
        }}
      >
        {/* Notification cards container with AnimatedList */}
        <Box
          sx={{
            position: 'absolute',
            top: { xs: 12, sm: 16 },
            right: { xs: 12, sm: 16 },
            left: { xs: 12, sm: 16 },
            bottom: { xs: 70, sm: 90 },
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <AnimatedList delay={1500}>
            {notifications.map((n, i) => (
              <NotificationItem key={`notification-${i}`} {...n} />
            ))}
          </AnimatedList>
        </Box>
      </Box>
    ),
  },
  {
    Icon: ShareIcon,
    name: 'Integrations',
    description: 'Supports 10+ payment providers and counting.',
    href: '#integrations',
    cta: 'View Integrations',
    className: 'col-span-2',
    background: <IntegrationsBackground />,
  },
  {
    Icon: GroupsIcon,
    name: 'Creator Management',
    description: 'Organize your entire creator roster with contracts, payments, and analytics.',
    href: '#collaborate',
    cta: 'Start Managing',
    className: 'col-span-1',
    background: (
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(180deg, rgba(16, 185, 129, 0.04) 0%, transparent 100%)',
        }}
      >
        {/* Animated creator avatars - responsive */}
        <Box sx={{ display: 'flex', gap: -1, position: 'relative' }}>
          {[0, 1, 2, 3, 4].map((i) => (
            <motion.div
              key={i}
              initial={{ scale: 0, x: -20 }}
              animate={{ scale: 1, x: 0 }}
              transition={{ delay: 0.1 * i, type: 'spring' }}
              style={{ marginLeft: i > 0 ? -10 : 0, zIndex: 5 - i }}
            >
              <Box
                sx={{
                  width: { xs: 36, sm: 42, md: 48 },
                  height: { xs: 36, sm: 42, md: 48 },
                  borderRadius: '50%',
                  background: `hsl(${200 + i * 30}, 70%, ${60 + i * 5}%)`,
                  border: { xs: '2px solid white', sm: '3px solid white' },
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: { xs: '0.6875rem', sm: '0.75rem', md: '0.875rem' },
                }}
              >
                {['JD', 'AK', 'MR', 'SL', '+12'][i]}
              </Box>
            </motion.div>
          ))}
        </Box>
      </Box>
    ),
  },
];

// Main Section Component - responsive
const BentoSection = () => {
  const theme = useTheme();
  return (
    <Box
      component={motion.section}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      sx={{
        py: { xs: 6, sm: 8, md: 12 },
        px: { xs: 2, sm: 3, md: 6 },
        background: theme.palette.background.default ,
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 6, md: 8 } }}>
          <Typography
            variant="h2"
            sx={{
              fontFamily: "Moret Variable",
              fontWeight: '600',
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem', lg: '62px' },
              background: 'linear-gradient(180deg, #0f172a 0%, #334155 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: { xs: 1.5, sm: 2 },
            }}
          >
            Everything You Need
          </Typography>
          <Typography
            sx={{
              color: '#64748b',
              fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
              maxWidth: 600,
              mx: 'auto',
              px: { xs: 2, sm: 0 },
            }}
          >
            A complete payment and creator management platform built for modern brands.
          </Typography>
        </Box>

        {/* Bento Grid */}
        <BentoGrid>
          {features.map((feature) => (
            <BentoCard key={feature.name} {...feature} />
          ))}
        </BentoGrid>
      </Box>
    </Box>
  );
};

export default BentoSection;
export { BentoGrid, BentoCard };